import React, { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, MapPin, Wallet, Languages, BookOpen, Save, RotateCcw, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import { API, api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const STEPS = [
  { key: "field", icon: BookOpen, q: "What do you want to study?",
    options: ["Business","Engineering","Fashion","Psychology","Computer Science","Humanities","Political Science","Other"] },
  { key: "budget", icon: Wallet, q: "What's your budget range?",
    options: ["Under €10k/year","€10–20k/year","€20k+/year"] },
  { key: "city", icon: MapPin, q: "Preferred city or region?",
    options: ["Paris","Lyon","Bordeaux","Toulouse","Nice / Riviera","Marseille","Strasbourg","Nantes","Grenoble","Rennes","Lille","Aix-en-Provence","Not sure yet"] },
  { key: "french_level", icon: Languages, q: "Your current French level?",
    options: ["None","Basic","Conversational","Fluent"] },
];

const STORAGE_KEY = "francepath:advisor-session:v1";
const EMPTY_INTAKE = { field:"", budget:"", city:"", french_level:"" };

function readLocal() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed !== "object" || !parsed) return null;
    return parsed;
  } catch { return null; }
}
function writeLocal(payload) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(payload)); } catch {}
}
function clearLocal() {
  try { localStorage.removeItem(STORAGE_KEY); } catch {}
}

function hasProgress(session) {
  if (!session) return false;
  if (session.result) return true;
  if (session.step > 0) return true;
  const i = session.intake || {};
  return !!(i.field || i.budget || i.city || i.french_level);
}

export default function PlanBuilder() {
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [intake, setIntake] = useState(EMPTY_INTAKE);
  const [streaming, setStreaming] = useState(false);
  const [raw, setRaw] = useState("");
  const [result, setResult] = useState(null);
  const [unis, setUnis] = useState([]);
  const [resumePrompt, setResumePrompt] = useState(null); // holds session offer to resume
  const [restoring, setRestoring] = useState(true);
  const abortRef = useRef(null);
  const savedRef = useRef(false); // avoid save loop during initial restore

  // Load universities (needed to render matches after restore)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/universities");
        setUnis(data);
      } catch {}
    })();
  }, []);

  // On mount: check local + backend for saved progress
  useEffect(() => {
    let cancelled = false;
    (async () => {
      let candidate = readLocal();
      // If logged in, prefer more-recent backend session (in case they used another device)
      if (user) {
        try {
          const { data } = await api.get("/advisor/session");
          if (hasProgress(data)) {
            const backend = { intake: data.intake, step: data.step, result: data.result, source: "cloud" };
            if (!candidate || hasProgress(backend)) candidate = backend;
          }
        } catch {}
      }
      if (cancelled) return;
      if (hasProgress(candidate)) {
        setResumePrompt(candidate);
      }
      setRestoring(false);
      // Delay marking saved-flag so restore doesn't trigger a save
      setTimeout(() => { savedRef.current = true; }, 100);
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Auto-save progress whenever intake / step / result changes
  useEffect(() => {
    if (!savedRef.current) return;
    if (streaming) return; // don't save mid-stream partials
    const payload = { intake, step, result, updated_at: new Date().toISOString() };
    if (!hasProgress(payload)) {
      clearLocal();
      return;
    }
    writeLocal(payload);
    if (user) {
      api.post("/advisor/session", { intake, step, result }).catch(() => {});
    }
  }, [intake, step, result, streaming, user]);

  const resumeSession = () => {
    if (!resumePrompt) return;
    setIntake({ ...EMPTY_INTAKE, ...(resumePrompt.intake || {}) });
    setStep(resumePrompt.step || 0);
    setResult(resumePrompt.result || null);
    setResumePrompt(null);
    toast.success("Picked up where you left off.");
  };

  const dismissResume = () => setResumePrompt(null);

  const startOver = async () => {
    setStep(0);
    setIntake(EMPTY_INTAKE);
    setResult(null);
    setRaw("");
    setResumePrompt(null);
    clearLocal();
    if (user) { try { await api.delete("/advisor/session"); } catch {} }
    toast.info("Fresh start.");
  };

  const select = (val) => {
    const key = STEPS[step].key;
    const next = { ...intake, [key]: val };
    setIntake(next);
    if (step < STEPS.length - 1) setStep(step + 1);
    else runAdvisor(next);
  };

  const runAdvisor = async (fin) => {
    setStreaming(true); setRaw(""); setResult(null);
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const resp = await fetch(`${API}/advisor/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...fin, start_year: "Fall 2026" }),
        signal: ctrl.signal,
      });
      const reader = resp.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      let full = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const p of parts) {
          const line = p.replace(/^data:\s*/, "").trim();
          if (!line) continue;
          try {
            const evt = JSON.parse(line);
            if (evt.delta) { full += evt.delta; setRaw(full); }
            if (evt.done) {
              try {
                const cleaned = full.replace(/^```json\s*/i, "").replace(/```$/, "").trim();
                const parsed = JSON.parse(cleaned);
                setResult(parsed);
              } catch (e) {
                console.warn("parse fail", e);
              }
            }
            if (evt.error) toast.error(evt.error);
          } catch {}
        }
      }
    } catch (e) {
      toast.error("Advisor stream failed. Please retry.");
    } finally {
      setStreaming(false);
    }
  };

  const savePlan = async () => {
    if (!user) { toast.info("Create an account to save your plan."); return; }
    if (!result) return;
    try {
      await api.post("/plans", { intake, matches: result.matches || [], ai_summary: result.summary });
      toast.success("Plan saved to your dashboard.");
    } catch {
      toast.error("Could not save plan.");
    }
  };

  const uniFor = (slug) => unis.find(u => u.slug === slug);
  const resumeStepLabel = resumePrompt
    ? (resumePrompt.result ? "your matched plan" : `question ${Math.min((resumePrompt.step || 0) + 1, STEPS.length)} of ${STEPS.length}`)
    : "";

  return (
    <div className="pt-28 md:pt-32 pb-24 min-h-screen" data-testid="page-advisor">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10">
        <div className="flex items-center gap-3 mb-6">
          <span className="rule-gold" /><span className="eyebrow">Free · 2 minutes · no account required</span>
        </div>
        <h1 className="serif text-5xl md:text-7xl leading-[0.95] tracking-tight max-w-4xl">
          Your personal <em className="italic text-[#b8892b]">advisor</em>. Answer four questions.
        </h1>
        <p className="text-lg text-[#4a5570] mt-4 max-w-2xl">
          Built from real French university data. Your personalised plan lands in seconds.
        </p>

        {/* Resume banner */}
        <AnimatePresence>
          {resumePrompt && !restoring && (
            <motion.div
              initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.35 }}
              className="mt-10 border border-[#b8892b] bg-white p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4"
              data-testid="advisor-resume-banner"
            >
              <div className="flex-1 flex items-start gap-4">
                <div className="w-10 h-10 shrink-0 bg-[#b8892b] text-white flex items-center justify-center">
                  <PlayCircle size={20} />
                </div>
                <div>
                  <div className="eyebrow !text-[#b8892b]">Pick up where you left off</div>
                  <div className="serif text-xl md:text-2xl text-[#0d1b3e] mt-1 leading-snug">
                    You have a saved session on {resumeStepLabel}.
                  </div>
                  <div className="text-xs text-[#4a5570] mt-1">
                    Saved {resumePrompt.source === "cloud" ? "to your account" : "on this device"}.
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={resumeSession} data-testid="advisor-resume-btn" className="btn-sharp btn-sharp-gold">
                  Resume
                </button>
                <button onClick={startOver} data-testid="advisor-resume-startover" className="btn-sharp btn-sharp-outline">
                  <RotateCcw size={14} /> Start over
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="mt-12 grid lg:grid-cols-12 gap-8">
          {/* Intake column */}
          <div className="lg:col-span-5">
            <div className="bg-white border border-[#0d1b3e]/10 p-8 lg:sticky lg:top-28">
              <div className="flex items-center justify-between mb-6">
                <div className="text-xs uppercase tracking-[0.22em] text-[#4a5570]">
                  Step {Math.min(step + 1, STEPS.length)} of {STEPS.length}
                </div>
                <div className="flex gap-1">
                  {STEPS.map((_, i) => (
                    <div key={i} className={`h-[3px] w-6 ${i <= step ? "bg-[#b8892b]" : "bg-[#0d1b3e]/10"}`} />
                  ))}
                </div>
              </div>
              <AnimatePresence mode="wait">
                {!streaming && !result && (
                  <motion.div key={step} initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-16 }} transition={{ duration: 0.35 }}>
                    <div className="flex items-center gap-2 text-[#b8892b] mb-3">
                      {React.createElement(STEPS[step].icon, { size: 16 })}
                      <span className="eyebrow !text-[#b8892b]">Question {step + 1}</span>
                    </div>
                    <h3 className="serif text-3xl text-[#0d1b3e] leading-tight">{STEPS[step].q}</h3>
                    <div className="mt-6 flex flex-col gap-2" data-testid="advisor-options">
                      {STEPS[step].options.map((o) => {
                        const chosen = intake[STEPS[step].key] === o;
                        return (
                          <button key={o} onClick={() => select(o)}
                            data-testid={`advisor-option-${o.toLowerCase().replace(/[^a-z0-9]+/g,"-")}`}
                            className={`text-left border px-4 py-3 transition-colors flex items-center justify-between ${chosen ? "bg-[#0d1b3e] text-white border-[#0d1b3e]" : "border-[#0d1b3e]/15 text-[#0d1b3e] hover:bg-[#0d1b3e] hover:text-white"}`}>
                            <span>{o}</span>
                            <ArrowRight size={14} className="opacity-40" />
                          </button>
                        );
                      })}
                    </div>
                    <div className="mt-6 flex items-center justify-between">
                      {step > 0 ? (
                        <button onClick={() => setStep(step - 1)} data-testid="advisor-back-btn" className="text-xs uppercase tracking-[0.2em] text-[#4a5570] hover:text-[#0d1b3e]">
                          ← Back
                        </button>
                      ) : <span />}
                      <span className="text-[10px] uppercase tracking-[0.22em] text-[#4a5570] flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#b8892b] inline-block" /> auto-saved
                      </span>
                    </div>
                  </motion.div>
                )}
                {(streaming || result) && (
                  <motion.div key="summary" initial={{ opacity:0 }} animate={{ opacity:1 }} className="space-y-3">
                    <div className="eyebrow">Your intake</div>
                    {Object.entries(intake).map(([k,v]) => (
                      <div key={k} className="flex justify-between border-b border-[#0d1b3e]/10 py-2 text-sm">
                        <span className="text-[#4a5570] capitalize">{k.replace("_"," ")}</span>
                        <span className="font-medium text-[#0d1b3e]">{v}</span>
                      </div>
                    ))}
                    <button onClick={startOver}
                      className="mt-4 btn-sharp btn-sharp-outline w-full justify-center" data-testid="advisor-restart">
                      <RotateCcw size={14} /> Start over
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Result column */}
          <div className="lg:col-span-7">
            {!streaming && !result && (
              <div className="border border-dashed border-[#0d1b3e]/20 p-10 h-full min-h-[400px] flex flex-col items-center justify-center text-center">
                <Sparkles size={28} className="text-[#b8892b] mb-4" />
                <div className="serif text-3xl text-[#0d1b3e] max-w-md">
                  Your personalised plan will stream in <em className="italic text-[#b8892b]">right here</em>.
                </div>
                <p className="text-sm text-[#4a5570] mt-3 max-w-md">
                  Matched universities, tuition and cost of living estimate, scholarship pointers, and a timeline.
                </p>
                <p className="text-xs text-[#4a5570] mt-6 max-w-md">
                  Your progress auto-saves, leave and come back whenever.
                </p>
              </div>
            )}
            {streaming && !result && (
              <div className="border border-[#0d1b3e]/15 bg-white p-8" data-testid="advisor-streaming">
                <div className="eyebrow mb-3">Thinking…</div>
                <pre className="whitespace-pre-wrap text-xs text-[#4a5570] font-mono leading-relaxed max-h-[500px] overflow-auto">
                  {raw || "Building your plan…"}
                </pre>
              </div>
            )}
            {result && (
              <motion.div initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }} className="space-y-8" data-testid="advisor-result">
                <div className="bg-[#0d1b3e] text-white p-8">
                  <div className="eyebrow text-[#b8892b] mb-3">Your plan</div>
                  <p className="serif text-2xl md:text-3xl leading-snug">{result.summary}</p>
                  <div className="mt-6 grid grid-cols-2 gap-6 border-t border-white/10 pt-6">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.22em] text-white/60">Estimated year-1 budget</div>
                      <div className="serif text-2xl text-[#b8892b] mt-1">{result.budget_estimate}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.22em] text-white/60">Matches</div>
                      <div className="serif text-2xl text-[#b8892b] mt-1">{result.matches?.length || 0} schools</div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="serif text-3xl text-[#0d1b3e] mb-4">Your matched universities</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {(result.matches || []).map((m) => {
                      const u = uniFor(m.slug);
                      if (!u) return null;
                      return (
                        <div key={m.slug} className="bg-white border border-[#0d1b3e]/10 p-5" data-testid={`advisor-match-${m.slug}`}>
                          <div className="aspect-[4/2] overflow-hidden -m-5 mb-4">
                            <img src={u.hero} alt={u.name} className="w-full h-full object-cover" />
                          </div>
                          <div className="text-xs text-[#4a5570]">{u.city} · {u.level === "master" ? "Master's" : "Bachelor's"}</div>
                          <div className="serif text-xl text-[#0d1b3e] mt-1">{u.name}</div>
                          <div className="mt-3 text-sm text-[#0d1b3e]/70">
                            <span className="text-[#b8892b] mr-1">·</span> Why this matched: {m.why}
                          </div>
                          <div className="mt-3 text-xs text-[#4a5570]">Tuition: €{u.tuition_eur.toLocaleString()}/yr {u.english_taught ? "· English-taught available" : ""}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {result.timeline && (
                  <div>
                    <h3 className="serif text-3xl text-[#0d1b3e] mb-4">Suggested timeline</h3>
                    <div className="border-t border-[#0d1b3e]/15">
                      {result.timeline.map((t, i) => (
                        <div key={i} className="grid grid-cols-12 border-b border-[#0d1b3e]/10 py-4">
                          <div className="col-span-4 text-sm uppercase tracking-[0.16em] text-[#b8892b]">{t.when}</div>
                          <div className="col-span-8 text-[#0d1b3e]">{t.step}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap gap-3">
                  <button onClick={savePlan} className="btn-sharp btn-sharp-gold" data-testid="advisor-save-plan">
                    <Save size={14} /> Save my plan
                  </button>
                  <Link to="/pricing" className="btn-sharp" data-testid="advisor-book-concierge">
                    Request a concierge invoice
                  </Link>
                  <Link to="/thrive" className="btn-sharp btn-sharp-outline" data-testid="advisor-join-community">
                    Join the community
                  </Link>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
